/**
 * "Switch user" BYLINE — whose name goes on a note, chat message or file.
 *
 * When a manager writes something while viewing as somebody else, the row is
 * still OWNED by the viewed account (that is what every scope filter,
 * permission check and read receipt reads, and none of that changes). Only
 * the name shown to a reader does: a note typed by the super admin used to
 * read "– ANOOJA", which is simply untrue.
 *
 * The server sends `switchedByUser` on those rows and nothing else. When it
 * is absent — the overwhelming majority — these helpers return the account
 * name unchanged, so every ordinary row looks exactly as it always did.
 */
export interface SwitchedByRef {
  id?: number | string | null;
  name?: string | null;
}

export interface Byline {
  /** The name to print: the real person when there is one. */
  name: string;
  /** The account it was written into, when that differs. Else null. */
  via: string | null;
}

export function resolveByline(
  accountName: string | null | undefined,
  switchedBy: SwitchedByRef | null | undefined,
): Byline {
  const account = (accountName ?? "").trim();
  const real = (switchedBy?.name ?? "").trim();
  if (!real) return { name: account, via: null };
  return { name: real, via: account || null };
}

/** One-line form: "Arjun · as ANOOJA", or just "ANOOJA". */
export function bylineText(
  accountName: string | null | undefined,
  switchedBy: SwitchedByRef | null | undefined,
): string {
  const { name, via } = resolveByline(accountName, switchedBy);
  return via ? `${name} · as ${via}` : name;
}

/** Initials for an avatar chip, from whichever name the byline picked. */
export function bylineInitials(
  accountName: string | null | undefined,
  switchedBy: SwitchedByRef | null | undefined,
): string {
  const { name } = resolveByline(accountName, switchedBy);
  return (
    (name || "?")
      .split(" ")
      .filter(Boolean)
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "?"
  );
}
